import { Select, Radio } from 'antd'
import _ from 'lodash'
import cx from 'classnames'
import update from 'immutability-helper'
import { Loader } from '@vzmi/shp_shared_compo'

import 'react-select/dist/react-select.min.css'
import styles from './BrandAnalysis.css'

import Popover from '../../Common/Popover'
import SPBaseModule from '../../../lib/SPBaseModule'
import { NoData } from '../../Common/StatePage'
import { SaleStatHelper } from './Shared'
import { getDefaultTimezoneMoment } from '../../../../lib/renderUtil'
import { getRangeAPIDate, getUIFormatDate } from '../../../lib/MomentHelper'
import { SALE_FILTER_TYPE_MAP } from '../../../constants/globalTypes'
import BrandChart from './BrandAnalysis/BrandChart'
import AllBrandChart from './BrandAnalysis/AllBrandChart'
import { PeriodInformation } from './PeriodInformation/PeriodInformation'
import FlexRow from '../../Common/FlexRow/FlexRow'
import {
  GA_CLICK_ACTION,
  getI13nModel
} from './BrandAnalysis.ga'
import { DEFAULT_DASHBOARD_DATE_RANGE } from '../../../constants/date'
import Block from '../../Common/NewDashboard/Block/Block'
import BrandAnalysisSearchForm from './BrandAnalysisSearchForm'
import Tabs from '../../Common/NewDashboard/Tabs/Tabs'

const { Option } = Select

export const DEFAULT_BRAND_OPTION_SELECT_ALL = {
  label: '整體概況',
  value: 'ALL'
}

export const SPECIFIC_BRAND = {
  label: '特定品牌',
  value: 'BRAND'
}

/** 將品牌分析中的unknown品牌改為未知品牌 */
const replaceUnknownBrandName = (brandName) => brandName === 'unknown' ? '未知品牌' : brandName

const NoResultData = () => (
  <NoData
    className={styles.noData}
    iconType='history'
    text='目前尚無資料，請稍候重試'
  />
)

/** 品牌分析 */
class BrandAnalysis extends SPBaseModule {
  constructor (props) {
    super(props)

    this.state = {
      periodText: '',
      brandSelectOptions: [],
      activeTab: DEFAULT_BRAND_OPTION_SELECT_ALL.value,
      brandId: '',
      sortType: SALE_FILTER_TYPE_MAP.AMOUNT.type,
      chartInfoMap: {
        ALL: []
      },
      chartDataMap: {
      }, // take brandId as key,
      customRange: DEFAULT_DASHBOARD_DATE_RANGE
    }
    this.searchFormProps = null
  }

  componentDidMount () {
    const { brandId } = this.state
    if (brandId && brandId !== DEFAULT_BRAND_OPTION_SELECT_ALL.value) {
      this.fetchBrandData(brandId)
    } else {
      // 先取得所有分區資料，並初始下拉選單
      this.searchFormProps.handleSubmit(this.onSearch)()
    }
  }

  setSearchFormProps = formProps => (this.searchFormProps = formProps)

  onSearch = values => this.loading(async () => {
    this.fetchAllBrandData(values)
  })

  onTabChange = activeTab => {
    const { brandId } = this.state
    const isAllTab = activeTab === DEFAULT_BRAND_OPTION_SELECT_ALL.value
    if (!isAllTab) {
      const chartDataByBrandId = this.getChartData(brandId)
      if (_.isEmpty(chartDataByBrandId) && !isAllTab) {
        this.fetchBrandData(brandId)
      }
    }

    this.sendPageviewByAction({
      type: GA_CLICK_ACTION.TAB,
      payload: activeTab
    })
    this.setState(() => ({ activeTab }))
  }

  onActiveKeyChange = stateName => value => {
    const brandId = value

    if (stateName === 'brandId') {
      const chartDataByBrandId = this.getChartData(brandId)
      const isAllBrand = brandId === DEFAULT_BRAND_OPTION_SELECT_ALL.value
      if (_.isEmpty(chartDataByBrandId) && !isAllBrand) {
        this.fetchBrandData(brandId)
      }
    }

    this.setState({
      [stateName]: brandId
    })
  }

  onSelectChange = brandId => {
    const chartDataByBrandId = this.getChartData(brandId)

    if (_.isEmpty(chartDataByBrandId)) {
      this.fetchBrandData(brandId)
    }

    this.sendPageviewByAction({
      type: GA_CLICK_ACTION.BRAND_DISTRIBUTION_DROPDOWN
    })
    this.setState(() => ({ brandId }))
  }

  onFormChange = (stateName) => (result) => {
    const value = result
    this.setState({
      [stateName]: value
    })
  }

  onSortTypeChange = e => {
    const value = _.get(e, 'target.value')

    const { sortType } = this.state
    const nextSortType = value
    if (sortType !== nextSortType) {
      this.sendPageviewByAction({
        type: GA_CLICK_ACTION.CHART_SORT,
        payload: value
      })
      this.setState({
        sortType: nextSortType
      })
    }
  }

  generateSelectOptions = data => {
    return _.map(
      data,
      entry => ({
        label: replaceUnknownBrandName(entry.brandName),
        value: entry.brandId
      })
    )
  }

  setChartData = (brandId, data) => {
    this.setState(update(this.state, {
      chartDataMap: {
        [brandId]: { $set: data }
      }
    }))
  }

  setChartInfo = (brandId, data) => {
    this.setState(update(this.state, {
      chartInfoMap: {
        [brandId]: { $set: data }
      }
    }))
  }

  getChartData = (brandId) => {
    // get by brandId
    const { chartDataMap } = this.state
    return _.get(chartDataMap, brandId)
  }

  getChartInfo = (brandId) => {
    // get by brandId
    const { chartInfoMap } = this.state
    return _.get(chartInfoMap, brandId)
  }

  /**
   * 第二層(單一品牌)：顯示前六個月之統計資料 資料每月十日更新
   * ex: 今天為 11/9, 資料統計區間：2017/4/1~2017/9/30 (預設為前六個月)
   * ex: 今天為 11/10 11:59:59, 資料統計區間：2017/4/1~2017/9/30 (預設為前六個月)
   * ex: 今天為 11/10 12:00:00, 資料統計區間：2017/5/1~2017/10/31 (預設為前六個月)
   */
  getPeriodByHalfYear = (apiDate) => {
    // 以每個月為單位
    // (1) 若 "現在"落在 當月10日 的 00:00~11:59 之間,
    //    (1-1) 若 api 的的日期 >= 前1個月的10日 , 則顯示時間用 "現在" 往前推 2個月/7個月
    //    (1-2) 若 api 的日期 < 前1個月的10日, 則顯示時間用 api 給的日期往前推 1個月/6個月
    // (2) 若 "現在" 沒有落在 當月10日 的 00:00~11:59 之間, 則皆用 api 給的日期, 往回推 1個月/6個月
    const now = getDefaultTimezoneMoment()
    // 基準日為每月10日中午
    const noonBaseForThisMonth = now.clone().set({ date: 10, hour: 12, minute: 0, second: 0, millisecond: 0 })
    const midnightBaseForThisMonth = noonBaseForThisMonth.clone().set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
    let baseMoment
    const apiDateMoment = getDefaultTimezoneMoment(apiDate).set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
    if (now.isBetween(midnightBaseForThisMonth, noonBaseForThisMonth)) {
      const noonBaseForLastMonthFromNow = noonBaseForThisMonth.clone().subtract(1, 'months').set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      if (apiDateMoment.isSameOrAfter(noonBaseForLastMonthFromNow)) {
        baseMoment = now.subtract(2, 'months').endOf('month')
      } else {
        baseMoment = apiDateMoment.subtract(1, 'months').endOf('month')
      }
    } else {
      baseMoment = apiDateMoment.subtract(1, 'months').endOf('month')
    }
    // six months ago of baseMoment
    const firstDayOfSixMonthAgo = baseMoment.clone().subtract(5, 'months').startOf('month')
    return {
      start: firstDayOfSixMonthAgo,
      end: baseMoment
    }
  }

  // 第一層(全部品牌)：MTD 之資料統計區間，預設為當月 1 日至當日前一天
  fetchAllBrandData = (dateRange, { reloadDropdown = true } = {}) => {
    const { mainRange } = dateRange
    // formatDate是call api，payload裡的period
    // mainRange：當前日期(起)/當前日期(迄) 這邊帶+8時區
    // 範例：'2023-03-01T00:00:00+08:00/2023-03-01T23:59:59+08:00'
    const formatDate = getRangeAPIDate(mainRange)
    const { fetchBrandDistribution } = this.props
    const { sortType } = this.state
    const { key } = SALE_FILTER_TYPE_MAP[sortType]
    const sort = `-${key}`

    let periodText = SaleStatHelper.formatPeriodSegment({
      start: getUIFormatDate(mainRange[0]),
      end: getUIFormatDate(mainRange[1])
    })

    const fetchPromise = this.promiseStateWrapper(
      fetchBrandDistribution({ period: formatDate, sort })
    )
      .then(res => {
        if (!res.error) {
          return _.get(res, 'payload.stats', null)
        }
      })
      .then(brandStat => {
        /** set up success state */
        this.setIsSuccess(true)

        if (brandStat) {
          if (!_.isEmpty(brandStat)) {
            const data = _.get(brandStat, '[0].orderBrandDistribution', [])
            const { periodSegment = {} } = _.get(brandStat, '0', {})
            periodText = _.isEmpty(periodSegment) ? periodText : SaleStatHelper.formatPeriodSegment(periodSegment)

            if (_.isEmpty(data)) {
              return
            }
            // 初始下拉選單
            if (reloadDropdown) {
              const brandSelectOptions = this.generateSelectOptions(data)
              const brandId = _.get(brandSelectOptions, '[0].value', null)
              this.setState({
                brandId,
                brandSelectOptions
              })

              // 若只有單一品牌則發動查詢，否則將認為是有第一層品牌資訊
              if (_.size(brandSelectOptions) === 1) {
                this.fetchBrandData(brandId)
              }

              this.setChartData(DEFAULT_BRAND_OPTION_SELECT_ALL.value, data)
              this.setChartInfo(DEFAULT_BRAND_OPTION_SELECT_ALL.value, `資料統計區間：${periodText} (預設為當月 1 日至當日前一天、每日中午更新)`)
            }
            return brandStat
          }
        }
      })

    return fetchPromise
  }

  // 第二層(單一品牌)：顯示前六個月之統計資料 資料每月十日更新
  fetchBrandData = (brandId) => {
    const { fetchBrandDistributionByBrandId } = this.props

    const fetchPromise = this.promiseStateWrapper(fetchBrandDistributionByBrandId({ brandId }))
      .then((res) => {
        if (!res.error) {
          return _.get(res, 'payload', null)
        }
      })
      .then((brandData) => {
      /** set up success state */
        this.setIsSuccess(true)
        if (brandData) {
          const { periodSegment = {} } = brandData
          const halfYearPeriod = this.getPeriodByHalfYear(periodSegment.start)
          // 預設為今日之前六個月的區間
          const periodText = _.isEmpty(periodSegment) ? SaleStatHelper.formatPeriodSegment(this.getPeriodByHalfYear(), true) : SaleStatHelper.formatPeriodSegment(halfYearPeriod)

          this.setChartData(brandId, brandData)
          this.setChartInfo(brandId, `資料統計區間：${periodText} (預設為前六個月、每月 10 日中午更新)`)
          return brandData
        }
      })
    return fetchPromise
  }

  computeChartData = () => {
    const {
      activeTab,
      brandId,
      sortType
    } = this.state

    const isAllTab = activeTab === DEFAULT_BRAND_OPTION_SELECT_ALL.value

    const RAW_DATA = this.getChartData(isAllTab ? activeTab : brandId)
    const { type: valueType, key: accessKey } = SALE_FILTER_TYPE_MAP[sortType]
    let data = _.map(RAW_DATA, (entry) => ({
      name: replaceUnknownBrandName(entry.brandName),
      value: _.get(entry, accessKey)
    }))

    // sort data by value in descending order
    data = _.orderBy(data, ['value'], ['desc'])

    return {
      valueType,
      data
    }
  }

  renderAllBrandChart = () => {
    const computedData = this.computeChartData()
    const { valueType, data } = computedData
    return _.isEmpty(data)
      ? <NoResultData />
      : (
        <AllBrandChart
          valueType={valueType}
          data={data}
        />
      )
  }

  renderBrandChart = () => {
    const {
      activeTab,
      brandId
    } = this.state

    const isAllTab = activeTab === DEFAULT_BRAND_OPTION_SELECT_ALL.value

    const data = this.getChartData(isAllTab ? activeTab : brandId)

    return _.isEmpty(data)
      ? <NoResultData />
      : (
        <BrandChart
          brandId={isAllTab ? activeTab : brandId}
          data={data}
        />
      )
  }

  renderChart = () => {
    const {
      isError,
      activeTab,
      sortType
    } = this.state
    // Getting isError state from parent class and render error page
    if (isError) {
      return this.renderIOError()
    }

    if (activeTab === DEFAULT_BRAND_OPTION_SELECT_ALL.value) {
      return (
        <section className={styles.allBrandSection}>
          <FlexRow className={styles.optionRow}>
            <Popover
              content='可依"訂購金額"或"訂購量"進行分析，其中"訂購金額"使用"訂購成本"進行統計'
            >
              品牌分佈
            </Popover>
            <span className={styles.radioButtonGroup}>

              <Radio
                id='brandFilterType1'
                name='brandFilterType'
                checked={sortType === SALE_FILTER_TYPE_MAP.AMOUNT.type}
                value={SALE_FILTER_TYPE_MAP.AMOUNT.type}
                onChange={this.onSortTypeChange}
              >
                {SALE_FILTER_TYPE_MAP.AMOUNT.text}
              </Radio>
              <Radio
                id='brandFilterType2'
                name='brandFilterType'
                checked={sortType === SALE_FILTER_TYPE_MAP.COUNT.type}
                value={SALE_FILTER_TYPE_MAP.COUNT.type}
                onChange={this.onSortTypeChange}
              >
                {SALE_FILTER_TYPE_MAP.COUNT.text}
              </Radio>
            </span>
          </FlexRow>
          <div>
            {this.renderAllBrandChart()}
          </div>
        </section>
      )
    }

    return this.renderBrandChart()
  }

  sendPageviewByAction = (action) => {
    const i13nModel = getI13nModel(action)
    this.props.sendDashboardPageview(i13nModel)
  }

  render () {
    const { className } = this.props
    const {
      brandSelectOptions,
      activeTab,
      brandId,
      isLoading,
      customRange
    } = this.state

    const isAllTab = activeTab === DEFAULT_BRAND_OPTION_SELECT_ALL.value

    const clz = cx(
      styles.brandAnalysis,
      {
        [className]: !!className
      }
    )

    return (
      <div
        className={clz}
        data-role='brand-analysis'
      >
        <Loader loaded={!isLoading}>
          <div className={styles.panel}>
            <Tabs
              type='tabs'
              animated={false}
              style={{ width: '100%' }}
              defaultActiveKey={DEFAULT_BRAND_OPTION_SELECT_ALL.value}
              onChange={this.onTabChange}
              items={
                // brandSelectOptions
                [
                  DEFAULT_BRAND_OPTION_SELECT_ALL,
                  SPECIFIC_BRAND
                ].map(({ value, label }) => ({ label, key: value }))
              }
            />
            {
              activeTab === DEFAULT_BRAND_OPTION_SELECT_ALL.value && (
                <Block
                  key='searchForm'
                >
                  <BrandAnalysisSearchForm
                    customRange={customRange}
                    onFormInit={this.setSearchFormProps}
                    onSubmit={this.onSearch}
                  />
                </Block>
              )
            }
            {
              activeTab === SPECIFIC_BRAND.value && (
                <>
                  <PeriodInformation>
                    {this.getChartInfo(isAllTab ? activeTab : brandId)}
                  </PeriodInformation>
                  <section className={styles.brandSelectSection}>
                    <Select
                      style={{ width: 220 }}
                      placeholder='請選擇'
                      value={brandId}
                      onChange={this.onSelectChange}
                    >
                      {
                        brandSelectOptions.map(
                          ({ value, label }) => (
                            <Option
                              key={value}
                              value={value}
                            >
                              {label}
                            </Option>
                          )
                        )
                      }
                    </Select>
                  </section>
                </>
              )
            }
          </div>
          {
            this.renderChart()
          }

        </Loader>
      </div>
    )
  }
}

export default BrandAnalysis
